/**
 * @file pages/well/DiagnosePage.js
 * @description Well Butler — "Diagnose / troubleshoot" (plan 06 §C.2 #2).
 *
 * The elimination game, phone-first. The backend diagnose engine is single-shot:
 * it scores ALL the evidence you have so far and returns the most-likely cause
 * (ranked) plus what to check next. So this screen gathers the field readings the
 * tech HAS, posts them, and shows the ranked answer with confidence + the verify
 * line. The tech can add another reading and re-run to narrow it — that's the
 * "one more measurement → narrows" loop, driven from the client.
 *
 * (A server-driven step-by-step wizard endpoint isn't in the backend yet; see the
 * report. This client-side gather-and-rerun flow matches the engine we have.)
 */
import React, { useState } from 'react';
import { Button, Input, Card, Badge } from '../../design-system';
import { wellApi } from '../../lib/wellApi';
import { pushHistory } from '../../lib/wellHistory';
import {
  WellPage, NumberedField, ResultHeader, Disclaimer, ErrorNote, Term,
} from './wellShared';

const num = (v) => (v === '' || v == null ? undefined : Number(v));

export default function DiagnosePage() {
  const [form, setForm] = useState({
    source: 'well',
    runs: '', // 'runs-normal' | 'wont-start' | 'wont-stop' | 'short-cycles' | 'dead-silent'
    flowLow: '',
    shortCycling: '',
    voltage: '',
    ratedVoltage: '230',
    amps: '',
    hp: '',
    ampTrend: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k, val) => setForm((f) => ({ ...f, [k]: f[k] === val ? '' : val }));

  async function run() {
    setLoading(true); setError(null);
    const evidence = {};
    if (form.runs) evidence.runs = form.runs;
    if (form.flowLow !== '') evidence.flowLow = form.flowLow === 'yes';
    if (form.shortCycling !== '') evidence.shortCycling = form.shortCycling === 'yes';
    if (form.voltage !== '') evidence.voltage = num(form.voltage);
    if (form.ratedVoltage !== '') evidence.ratedVoltage = num(form.ratedVoltage);
    if (form.amps !== '') evidence.amps = num(form.amps);
    if (form.ampTrend) evidence.ampTrend = form.ampTrend;

    const input = {
      source: form.source,
      evidence,
      motor: { hp: num(form.hp), voltage: num(form.ratedVoltage) },
    };
    try {
      const env = await wellApi.diagnose(input);
      if (env.success) {
        setResult(env.data);
        const lead = env.data.leading || {};
        pushHistory({
          kind: 'diagnose',
          title: lead.causeLabel || 'Diagnosis',
          summary: `Leading cause · ${lead.confidence ?? '?'}/95`,
          confidence: env.data.confidence,
          persistedId: env.meta && env.meta.persistedId,
        });
      } else {
        setError(env.error || { message: 'Could not run the diagnosis.' });
      }
    } catch (e) {
      setError({ message: e.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <WellPage
      title="Diagnose / troubleshoot"
      intro="Tell me what you see and measure. Give what you've got — the more readings, the surer the answer. Add one more and re-run to narrow it down."
    >
      <Card variant="elevated" padding="md">
        <NumberedField n={1} label="Water source">
          <Choice value={form.source} onPick={(v) => setForm((f) => ({ ...f, source: v }))}
            options={[['well', 'Well pump'], ['city', 'City water']]} />
        </NumberedField>

        <NumberedField n={2} label="What's the pump doing?">
          <Choice value={form.runs} onPick={(v) => setForm((f) => ({ ...f, runs: f.runs === v ? '' : v }))}
            options={[
              ['runs-normal', 'Runs, seems normal'],
              ['wont-start', "Won't start"],
              ['dead-silent', 'Dead silent'],
              ['wont-stop', "Runs and won't shut off"],
              ['short-cycles', 'Clicks on/off fast'],
            ]} />
        </NumberedField>

        <NumberedField n={3} label="Is the flow weak?" hint="Low pressure or barely any water at the fixtures.">
          <YesNo value={form.flowLow} onPick={(v) => setBool('flowLow', v)} />
        </NumberedField>

        <NumberedField
          n={4}
          label="Short-cycling?"
          hint="The pump kicks on and off every few seconds instead of running a steady stretch — usually a waterlogged or bad pressure tank."
        >
          <YesNo value={form.shortCycling} onPick={(v) => setBool('shortCycling', v)} />
        </NumberedField>

        <NumberedField
          n={5}
          label="Line voltage at the switch"
          hint="Volts measured at the pressure switch with the pump trying to run. Leave blank if you didn't check."
        >
          <Input type="number" inputMode="decimal" value={form.voltage} onChange={set('voltage')} rightAddon="V" />
        </NumberedField>

        <NumberedField n={6} label="Rated voltage on the motor tag" hint="Usually 115, 230, or 460.">
          <Input type="number" inputMode="numeric" value={form.ratedVoltage} onChange={set('ratedVoltage')} rightAddon="V" />
        </NumberedField>

        <NumberedField
          n={7}
          label="Running amps (clamp meter)"
          hint={
            <>Clamp one of the motor leads while it runs. We compare it to the motor's{' '}
              <Term word="FLA">Full-Load Amps — the most current the motor should pull at normal load. On the motor tag.</Term>.
            </>
          }
        >
          <Input type="number" inputMode="decimal" value={form.amps} onChange={set('amps')} rightAddon="A" />
        </NumberedField>

        <NumberedField n={8} label="Motor horsepower (from the tag)" hint="Helps match the right amp thresholds.">
          <Input type="number" inputMode="decimal" value={form.hp} onChange={set('hp')} rightAddon="HP" />
        </NumberedField>

        <NumberedField n={9} label="Amp trend while running" hint="Optional — watch the clamp meter for 30 seconds.">
          <Choice value={form.ampTrend} onPick={(v) => setForm((f) => ({ ...f, ampTrend: f.ampTrend === v ? '' : v }))}
            options={[['steady', 'Steady'], ['dropping', 'Dropping'], ['climbing', 'Climbing']]} />
        </NumberedField>

        <Button fullWidth size="lg" loading={loading} onClick={run} style={{ marginTop: 'var(--space-2)' }}>
          {result ? 'Re-run with what I added' : 'Find the likely cause'}
        </Button>
      </Card>

      {error && <div style={{ marginTop: 'var(--space-4)' }}><ErrorNote error={error} /></div>}

      {result && <DiagnoseResult data={result} />}
    </WellPage>
  );
}

function DiagnoseResult({ data }) {
  const lead = data.leading || {};
  const runners = Array.isArray(data.runnersUp) ? data.runnersUp : [];
  return (
    <div style={{ marginTop: 'var(--space-5)' }}>
      <ResultHeader
        answer={lead.causeLabel || 'Not enough measured yet'}
        sub={lead.leadIn}
        confidence={data.confidence}
      />

      {Array.isArray(data.guardBanners) && data.guardBanners.length > 0 && (
        <Card variant="outline" padding="md" style={{ marginBottom: 'var(--space-3)', borderColor: 'var(--border-warning, var(--border-strong))' }}>
          {data.guardBanners.map((b, i) => (
            <p key={i} style={{ margin: i ? 'var(--space-2) 0 0' : 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}>⚠ {b}</p>
          ))}
        </Card>
      )}

      {(lead.actionPlain || lead.verifyStep) && (
        <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-2)' }}>What to do / check next</div>
          {lead.verifyStep && (
            <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)', lineHeight: 1.5 }}>
              <strong>Verify it:</strong> {lead.verifyStep}
            </p>
          )}
          {lead.actionPlain && (
            <p style={{ margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <strong>Then:</strong> {lead.actionPlain}
            </p>
          )}
          {data.pullRequired && (
            <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-body-sm)', color: 'var(--text-error)', fontWeight: 'var(--weight-semibold)' }}>
              This points to pulling the pump — confirm the surface checks first so you don't pull for nothing.
            </p>
          )}
        </Card>
      )}

      {runners.length > 0 && (
        <Card variant="outline" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-2)' }}>Also possible</div>
          {runners.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-1) 0' }}>
              <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>{r.causeLabel || r.causeId}</span>
              <Badge color="gray" variant="subtle" size="sm">{r.confidence ?? '?'}/95</Badge>
            </div>
          ))}
        </Card>
      )}

      {Array.isArray(data.remainingSurfaceSteps) && data.remainingSurfaceSteps.length > 0 && (
        <Card variant="flat" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-2)' }}>
            Add one of these to narrow it (then re-run)
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', lineHeight: 1.6 }}>
            {data.remainingSurfaceSteps.map((s, i) => <li key={i}>{typeof s === 'string' ? s : (s.label || s.step || JSON.stringify(s))}</li>)}
          </ul>
        </Card>
      )}

      <Disclaimer text={data.disclaimer} />
    </div>
  );
}

/* --- small input helpers (big tap targets, one-handed) --- */
function Choice({ value, onPick, options }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {options.map(([val, label]) => (
        <button
          key={val}
          type="button"
          onClick={() => onPick(val)}
          style={chipStyle(value === val)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function YesNo({ value, onPick }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      <button type="button" onClick={() => onPick('yes')} style={chipStyle(value === 'yes')}>Yes</button>
      <button type="button" onClick={() => onPick('no')} style={chipStyle(value === 'no')}>No</button>
    </div>
  );
}

function chipStyle(active) {
  return {
    minHeight: 44, padding: '0 var(--space-4)', borderRadius: 'var(--radius-lg)', cursor: 'pointer',
    font: 'inherit', fontWeight: 'var(--weight-semibold)',
    border: `2px solid ${active ? 'var(--color-primary)' : 'var(--border-default)'}`,
    background: active ? 'var(--color-primary)' : 'var(--surface-raised)',
    color: active ? '#fff' : 'var(--text-primary)',
  };
}
